import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { renderAsync } from 'docx-preview';
import { Document } from '../../types/types';
import { candidateService } from '../../services/api/candidateService';

interface DocumentViewerModalProps {
    document: Document | null;
    candidateId: string;
    onHide: () => void;
}

type ViewerType = 'pdf' | 'docx';

export const getViewerType = (fileName: string): ViewerType | null => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
        case 'pdf':
            return 'pdf';
        case 'docx':
            return 'docx';
        default:
            return null;
    }
};

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
    document,
    candidateId,
    onHide
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
    const docxContainerRef = useRef<HTMLDivElement>(null);

    const viewerType = document ? getViewerType(document.fileName) : null;

    useEffect(() => {
        if (!document || !viewerType) return;

        let cancelled = false;
        let objectUrl: string | null = null;

        const loadDocument = async () => {
            setLoading(true);
            setError(null);

            try {
                const blob = await candidateService.downloadDocument(candidateId, document.id);
                if (cancelled) return;

                if (viewerType === 'pdf') {
                    // Re-type the blob so the browser's PDF viewer renders it inline
                    objectUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    setPdfUrl(objectUrl);
                } else {
                    setDocxBlob(blob);
                }
            } catch (err) {
                console.error('Error loading document for viewing:', err);
                if (!cancelled) setError('Failed to load the document. Please try downloading it instead.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadDocument();

        return () => {
            cancelled = true;
            if (objectUrl) window.URL.revokeObjectURL(objectUrl);
            setPdfUrl(null);
            setDocxBlob(null);
        };
    }, [document, viewerType, candidateId]);

    // Render the docx once both the blob and the container are available
    useEffect(() => {
        const container = docxContainerRef.current;
        if (!docxBlob || !container) return;

        container.innerHTML = '';
        renderAsync(docxBlob, container, undefined, {
            inWrapper: true,
            ignoreLastRenderedPageBreak: true
        }).catch(err => {
            console.error('Error rendering docx:', err);
            setError('Failed to display the document. Please try downloading it instead.');
        });
    }, [docxBlob]);

    return (
        <Modal show={!!document} onHide={onHide} fullscreen>
            <Modal.Header closeButton>
                <Modal.Title className="text-truncate">
                    <i className={`bi ${viewerType === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-word'} me-2`}></i>
                    {document?.fileName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 d-flex flex-column bg-light">
                {loading && (
                    <div className="d-flex justify-content-center align-items-center flex-grow-1">
                        <Spinner animation="border" />
                    </div>
                )}

                {error && (
                    <Alert variant="danger" className="m-3">
                        <i className="bi bi-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {viewerType === 'pdf' && pdfUrl && !error && (
                    <iframe
                        src={pdfUrl}
                        title={document?.fileName}
                        className="flex-grow-1 w-100 border-0"
                    />
                )}

                {viewerType === 'docx' && (
                    <div
                        ref={docxContainerRef}
                        className="flex-grow-1 overflow-auto"
                        style={{ display: loading || error ? 'none' : 'block' }}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DocumentViewerModal;
